import { Skeleton, ThemeProvider, TextField, Box, Chip, Typography, Button, Autocomplete } from "@mui/material";
import { useContext, useEffect, useState, useMemo, useRef, forwardRef } from "react";
import { Search, CheckCircle } from "@mui/icons-material";
import { ThemeContext } from "../../App";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";

const TallListbox = forwardRef(function TallListbox(props, ref) {
    const { style, ...rest } = props;
    return (
        <ul
            {...rest}
            ref={ref}
            style={{ ...style, maxHeight: '60vh', overflow: 'auto' }}
        />
    );
});

// Gets columns and associated values into easily searchable data-structure
function flattenFieldsForQuickSearch(fields) {
    const flattenedColumns = [];
    
    fields.forEach(category => {
        const categoryLabel = category.label || '';
        
        if (category.options && Array.isArray(category.options)) {
            category.options.forEach(column => {
                if (column.name && column.values && Array.isArray(column.values)) {
                    const columnValues = column.values
                        .filter(value => value && (value.name || value.label))
                        .map(value => ({
                            value: value.name || value.label,
                            display: value.label || value.name
                        }));
                    
                    if (columnValues.length > 0) {
                        flattenedColumns.push({
                            field: column.name,
                            label: column.label || column.name,
                            category: categoryLabel,
                            values: columnValues,
                            operators: column.operators || []
                        });
                    }
                }
            });
        }
    });
    
    return flattenedColumns.sort((colObj1, colObj2) => {
        if (colObj1.field < colObj2.field) {
            return -1;
        }
        if (colObj1.field > colObj2.field) {
            return 1;
        }
        return 0;
    });
}

function QuickSearchFilter({ fields, onAddRule, quickQuery, onQueryChange }) {
    const [searchValue, setSearchValue] = useState('');
    const [pinnedSearch, setPinnedSearch] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const ignoreNextEmptyInputRef = useRef(false);

    const flattenedColumns = useMemo(() => {
        return flattenFieldsForQuickSearch(fields);
    }, [fields]);

    const effectiveSearch = useMemo(() => {
        const typed = searchValue.trim();
        if (typed.length >= 3) return typed;
        const pinned = (pinnedSearch || '').trim();
        if (pinned.length >= 3) return pinned;
        return '';
    }, [searchValue, pinnedSearch]);

    // Get field label for display in chips
    const getFieldLabel = (fieldName) => {
        const column = flattenedColumns.find(col => col.field === fieldName);
        return column ? column.label : fieldName;
    };

    // Create select options from matching values with category for grouping
    const selectOptions = useMemo(() => {
        if (effectiveSearch.length < 3 || !effectiveSearch || flattenedColumns.length === 0) return [];

        const options = [];
        const q = effectiveSearch.toLowerCase();

        flattenedColumns.forEach(column => {
            const matchingValues = column.values.filter(valueObj =>
                valueObj.value.toString().toLowerCase().includes(q)
            ).sort((valueObj1, valueObj2) => {return valueObj1.field > valueObj2.field});

            matchingValues.forEach(valueObj => {
                options.push({
                    value: `${column.field}|${valueObj.value}`,
                    label: valueObj.display,
                    sublabel: `${column.label} - ${column.category}`,
                    field: column.field,
                    category: column.label,
                    valueObj: valueObj
                });
            });
        });

        return options.slice(0, 500);
    }, [effectiveSearch, flattenedColumns]);

    const handleAutocompleteChange = (event, newValue) => {
        if (newValue) {
            const newRule = {
                field: newValue.field,
                operator: "=",
                valueSource: "value",
                value: newValue.valueObj.value
            };
            const existingIndex = quickQuery.rules?.findIndex(rule =>
                rule.field === newRule.field &&
                rule.value === newRule.value &&
                rule.operator === "="
            ) ?? -1;
            if (existingIndex >= 0) {
                removeRule(existingIndex);
            } else {
                onAddRule(newRule);
            }
            setPinnedSearch((prev) => (searchValue.trim().length >= 3 ? searchValue.trim() : prev));
            ignoreNextEmptyInputRef.current = true;
            setSearchValue('');
            setIsOpen(true);
        }
    };

    
    const removeRule = (ruleIndex) => {
        const updatedQuery = {
            ...quickQuery,
            rules: quickQuery.rules.filter((_, index) => index !== ruleIndex)
        };
        onQueryChange(updatedQuery);
    };

    const updateCombinator = (combinator) => {
        const updatedQuery = {
            ...quickQuery,
            combinator: combinator
        };
        onQueryChange(updatedQuery);
    };

    // Check if an option is already selected
    const isOptionSelected = (option) => {
        if (!quickQuery.rules || quickQuery.rules.length === 0) return false;
        return quickQuery.rules.some(rule => 
            rule.field === option.field && 
            rule.value === option.valueObj.value &&
            rule.operator === "="
        );
    };

    return (
        <Box>
            {/* Search Bar with Integrated Dropdown */}
            <Box sx={{ position: 'relative', mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {/* Combinator Controls - Pill Selector */}
                    <Box
                        sx={{
                            display: "inline-flex",
                            borderRadius: "9999px",
                            border: "1px solid",
                            borderColor: "#000270",
                            overflow: "hidden",
                            bgcolor: "background.paper",
                            flexShrink: 0,
                        }}
                    >
                        <Button
                            size="small"
                            onClick={() => updateCombinator('and')}
                            startIcon={quickQuery.combinator === 'and' ? <CheckCircle sx={{ fontSize: "1.125rem", color: "#000270" }} /> : null}
                            sx={{
                                minWidth: 76,
                                py: 0.75,
                                fontSize: "0.875rem",
                                borderRadius: 0,
                                textTransform: "none",
                                fontWeight: quickQuery.combinator === 'and' ? 600 : 400,
                                ...(quickQuery.combinator === 'and'
                                    ? { bgcolor: "#bbdefb", color: "#000270", "&:hover": { bgcolor: "#90caf9" } }
                                    : { bgcolor: "background.paper", color: "#000270", "&:hover": { bgcolor: "rgba(0, 2, 112, 0.04)" } }),
                            }}
                        >
                            AND
                        </Button>
                        <Box sx={{ width: 1, borderLeft: "1px solid #000270", alignSelf: "stretch" }} />
                        <Button
                            size="small"
                            onClick={() => updateCombinator('or')}
                            startIcon={quickQuery.combinator === 'or' ? <CheckCircle sx={{ fontSize: "1.125rem", color: "#000270" }} /> : null}
                            sx={{
                                minWidth: 76,
                                py: 0.75,
                                fontSize: "0.875rem",
                                borderRadius: 0,
                                textTransform: "none",
                                fontWeight: quickQuery.combinator === 'or' ? 600 : 400,
                                ...(quickQuery.combinator === 'or'
                                    ? { bgcolor: "#bbdefb", color: "#000270", "&:hover": { bgcolor: "#90caf9" } }
                                    : { bgcolor: "background.paper", color: "#000270", "&:hover": { bgcolor: "rgba(0, 2, 112, 0.04)" } }),
                            }}
                        >
                            OR
                        </Button>
                    </Box>

                    <Box sx={{ flex: 1, position: 'relative' }}>
                        <Autocomplete
                            fullWidth
                            size="small"
                            options={selectOptions}
                            value={null}
                            onChange={handleAutocompleteChange}
                            onInputChange={(event, newInputValue, reason) => {
                                if (reason === 'reset') return;
                                if (newInputValue === '' && ignoreNextEmptyInputRef.current) {
                                    ignoreNextEmptyInputRef.current = false;
                                    setSearchValue('');
                                    return;
                                }
                                ignoreNextEmptyInputRef.current = false;
                                setSearchValue(newInputValue);
                                const t = newInputValue.trim();
                                if (t.length < 3) {
                                    setPinnedSearch(null);
                                } else {
                                    setPinnedSearch(null);
                                    setIsOpen(true);
                                }
                            }}
                            inputValue={searchValue}
                            open={isOpen && effectiveSearch.length >= 3 && selectOptions.length > 0}
                            onOpen={() => {
                                if (effectiveSearch.length >= 3 && selectOptions.length > 0) {
                                    setIsOpen(true);
                                }
                            }}
                            onClose={(event, reason) => {
                                if (reason !== 'selectOption') {
                                    setIsOpen(false);
                                }
                                if (reason === 'blur' || reason === 'escape') {
                                    setPinnedSearch(null);
                                    setSearchValue('');
                                }
                            }}
                            groupBy={(option) => option.category || 'Other'}
                            getOptionLabel={(option) => option.label || ''}
                            isOptionEqualToValue={(option, value) => 
                                option.value === value?.value
                            }
                            filterOptions={(options) => {
                                if (effectiveSearch.length < 3) return [];
                                return options;
                            }}
                            clearOnBlur={false}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    label="Quick Search"
                                    placeholder="Select..."
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <>
                                                <Search sx={{ mr: 1, color: 'action.active' }} />
                                                {quickQuery.rules && quickQuery.rules.length > 0 && quickQuery.rules.map((rule, index) => {
                                                    const fieldLabel = getFieldLabel(rule.field);
                                                    return (
                                                        <Chip
                                                            key={index}
                                                            label={`${fieldLabel} | ${rule.value}`}
                                                            onDelete={(e) => {
                                                                e.stopPropagation();
                                                                removeRule(index);
                                                            }}
                                                            size="small"
                                                            sx={{
                                                                height: '24px',
                                                                backgroundColor: '#bbdefb',
                                                                color: '#000270',
                                                                borderRadius: '12px',
                                                                mr: 0.5,
                                                                '& .MuiChip-deleteIcon': {
                                                                    color: '#000270',
                                                                    fontSize: '16px',
                                                                    '&:hover': {
                                                                        color: '#000270',
                                                                    },
                                                                },
                                                            }}
                                                        />
                                                    );
                                                })}
                                                {params.InputProps.startAdornment}
                                            </>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '16px',
                                            '& fieldset': {
                                                borderColor: '#000270',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: '#000270',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#000270',
                                            },
                                        },
                                    }}
                                />
                            )}
                            renderOption={(props, option) => {
                                const isSelected = isOptionSelected(option);
                                const matches = match(option.label, effectiveSearch, {
                                    insideWords: true,
                                });
                                const parts = parse(option.label, matches);

                                return (
                                    <li {...props}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                width: '100%',
                                                gap: 1.5
                                            }}
                                        >
                                            {isSelected && (
                                                <CheckCircle 
                                                    sx={{ 
                                                        color: 'primary.main',
                                                        fontSize: '1.25rem',
                                                        flexShrink: 0
                                                    }} 
                                                />
                                            )}
                                            <Box sx={{ flex: 1 }}>
                                                {parts.map((part, index) => (
                                                    <span
                                                        key={index}
                                                        style={{
                                                            fontWeight: part.highlight ? 700 : 400,
                                                        }}
                                                    >
                                                        {part.text}
                                                    </span>
                                                ))}
                                            </Box>
                                        </Box>
                                    </li>
                                );
                            }}
                            ListboxComponent={TallListbox}
                            noOptionsText={effectiveSearch.length < 3 ? "Type at least 3 characters to search" : "No options found"}
                        />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}



export function QuickQueryBuilder(props) {
    const { fields, quickQuery, setQuickQuery, setIsQueryValid } = { ...props };
    const { theme } = useContext(ThemeContext);

    // Flatten fields for search purposes
    const flattenedFields = useMemo(() => {
        return flattenFieldsForQuickSearch(fields);
    }, [fields]);

    useEffect(() => {
        console.log('fields', fields);
        console.log('flattened fields', flattenedFields);
    }, [fields, flattenedFields]);
 
    useEffect(() => {
        console.log('query', quickQuery);
    }, [quickQuery]);

    const handleQueryChange = (newQuery) => {
        // Ensure the query has the correct structure
        const formattedQuery = {
            combinator: newQuery.combinator || 'and',
            rules: newQuery.rules || []
        };
        
        let hasEmptyValue = false;
        
        if (formattedQuery.rules && Array.isArray(formattedQuery.rules)) {
            hasEmptyValue = formattedQuery.rules.some((rule) => {
                if (rule.rules) return false;
                
                return rule.value === null || 
                       rule.value === "" || 
                       rule.value === undefined ||
                       (Array.isArray(rule.value) && rule.value.length === 0);
            });
        }
        
        setIsQueryValid(!hasEmptyValue);
        setQuickQuery(formattedQuery);
    };

    const handleQuickFilterAdd = (newRule) => {
        const currentRules = quickQuery.rules || [];
        const updatedQuery = {
            combinator: quickQuery.combinator || 'and',
            rules: [...currentRules, newRule]
        };
        handleQueryChange(updatedQuery);
    };

    return (
        <>
            {(Array.isArray(fields) && fields.length === 0)
                ? <Skeleton variant="rectangular" height={41} />
                : <ThemeProvider theme={theme}>
                    <QuickSearchFilter
                        fields={fields}
                        onAddRule={handleQuickFilterAdd}
                        quickQuery={quickQuery}
                        onQueryChange={handleQueryChange}
                    />
                </ThemeProvider>        
            }
        </>
    );
}